import React, { useState } from 'react';
import {
    Box,
    Modal,
    Backdrop,
    Fade,
    IconButton,
    Typography,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    Divider,
    TextField,
    InputAdornment,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    Close as CloseIcon,
    Search as SearchIcon,
    ExpandMore as ExpandMoreIcon,
    MenuBook as MenuBookIcon,
} from '@mui/icons-material';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '95%', sm: '90%', md: '85%', lg: '80%' },
    height: { xs: '90%', sm: '85%', md: '80%' },
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
};

const UserManualModal = ({ open, onClose }) => {
    const [selectedTab, setSelectedTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedAccordion, setExpandedAccordion] = useState(false);

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
        setSearchTerm('');
    };

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedAccordion(isExpanded ? panel : false);
    };

    const manualSections = [
        {
            category: 'Getting Started',
            items: [
                {
                    title: 'Introduction',
                    content: `Scholiq is a comprehensive School Management System designed to streamline and automate various administrative and academic processes in educational institutions.

Key Features:
• Real-time Dashboard & Analytics
• Student & Staff Management
• Attendance Tracking
• Fee Management
• Examination & Grading
• Library Management
• Transport Management
• Communication Tools
• AI-Powered Features
• Mobile Responsive Design`
                },
                {
                    title: 'Login & Authentication',
                    content: `First Time Login:
1. Navigate to Landing Page
2. Click Login Button
3. Enter your Email Address and Password
4. Click Submit

Forgot Password:
1. Click "Forgot Password?" link
2. Enter registered email
3. Follow email instructions
4. Set new password

Password Requirements:
• Minimum 8 characters
• Include uppercase and lowercase
• At least one number
• Special characters (recommended)`
                },
                {
                    title: 'Dashboard Overview',
                    content: `The Dashboard provides:
• Statistics Cards - Key metrics overview
• Quick Actions - Shortcuts to features
• Recent Activities - Latest updates
• Calendar View - Events and holidays
• Announcements - Important messages

Navigation:
• Use sidebar menu for modules
• Click statistics for detailed views
• Bell icon for notifications
• Search bar for quick access`
                }
            ]
        },
        {
            category: 'Student Management',
            items: [
                {
                    title: 'Adding Students',
                    content: `Steps to Add a New Student:
1. Navigate to Student Module
2. Click "Add Student" Button
3. Fill in Details:
   • Personal Information
   • Contact Details
   • Address
   • Parent/Guardian Info
   • Academic Information
   • Medical Information
   • Upload photograph
4. Submit the form

The system will generate a unique student ID automatically.`
                },
                {
                    title: 'Managing Student Records',
                    content: `View Student Details:
1. Go to Student module
2. Search or filter students
3. Click student name
4. Access tabs for:
   • Personal Info
   • Academic Records
   • Attendance
   • Fee Status
   • Exam Results
   • Documents

Edit Student Information:
1. Open student profile
2. Click Edit button
3. Modify fields
4. Save changes`
                },
                {
                    title: 'Student Promotion',
                    content: `Promote Students to Next Class:
1. Master → Promotion
2. Select current class/section
3. Choose students to promote
4. Select target class
5. Confirm promotion

Bulk promotion available for entire class.`
                }
            ]
        },
        {
            category: 'Staff Management',
            items: [
                {
                    title: 'Adding Staff',
                    content: `Steps to Add New Staff:
1. Click Staff in sidebar
2. Click "Add Staff"
3. Fill in:
   • Personal Information
   • Contact Details
   • Designation/Role
   • Qualifications
   • Employment Details
   • Salary Information
   • Documents and photo
4. Submit

Staff will receive login credentials via email.`
                },
                {
                    title: 'Staff Salary Management',
                    content: `Manage Staff Salaries:
1. Navigate to Staff Salary module
2. View salary structure
3. Process monthly salaries
4. Generate salary slips
5. Track payment history

Features:
• Automatic calculations
• Tax deductions
• Allowances management
• Payment history tracking`
                }
            ]
        },
        {
            category: 'Attendance',
            items: [
                {
                    title: 'Marking Student Attendance',
                    content: `Mark Daily Attendance:
1. Attendance → Student
2. Select class and section
3. Select date
4. Mark status:
   • Present
   • Absent
   • Late
   • Half Day
5. Add remarks (optional)
6. Submit

Bulk actions available for faster marking.`
                },
                {
                    title: 'Attendance Reports',
                    content: `Generate Attendance Reports:
1. Select date range
2. Choose class/section
3. View statistics:
   • Daily attendance
   • Monthly summary
   • Individual student records
   • Defaulter list
4. Export (PDF/Excel)

Reports show percentage and trends.`
                },
                {
                    title: 'Leave Management',
                    content: `Apply for Leave:
1. Attendance → Leave
2. Select leave type
3. Choose dates
4. Provide reason
5. Submit request

Approve/Reject (Admin):
1. View pending requests
2. Check details
3. Approve or reject
4. Add comments

View leave balance and history in profile.`
                }
            ]
        },
        {
            category: 'Fee Management',
            items: [
                {
                    title: 'Fee Collection',
                    content: `Collect Fee Payment:
1. Fee → Fee Deposit
2. Search student
3. View fee dues
4. Select components
5. Enter payment:
   • Amount
   • Mode (Cash/Cheque/Online)
   • Transaction ID
6. Generate receipt
7. Print/Email receipt

Partial payments allowed with tracking.`
                },
                {
                    title: 'Fee Types & Structure',
                    content: `Create Fee Types:
1. Fee → Create Fee Type
2. Define fee name
3. Add description
4. Save

Set Fee Amount:
1. Fee → Set Fee Amount
2. Select class/section
3. Choose fee type
4. Enter amount
5. Apply to students

Different structures for different classes.`
                },
                {
                    title: 'Fee Reports',
                    content: `Generate Fee Reports:
1. Fee → Fee Report
2. Select filters:
   • Date range
   • Class/Section
   • Fee type
   • Payment status
3. View analytics
4. Export data

Reports include:
• Collection summary
• Outstanding dues
• Payment history
• Defaulter list`
                }
            ]
        },
        {
            category: 'Examination',
            items: [
                {
                    title: 'Creating Exams',
                    content: `Create New Exam:
1. Exam → Create/Edit Exam
2. Click "Create Exam"
3. Fill details:
   • Exam name
   • Academic year
   • Dates
   • Classes
4. Add subjects
5. Set marks distribution
6. Save

Multiple exam types supported.`
                },
                {
                    title: 'Grade Entry',
                    content: `Enter Exam Marks:
1. Exam → Grading
2. Select exam and subject
3. Choose class/section
4. Enter marks for students
5. System calculates grades
6. Submit marks

Bulk entry available via Excel import.`
                },
                {
                    title: 'Marksheet Generation',
                    content: `Generate Marksheets:
1. Exam → MarkSheet
2. Select exam
3. Choose students
4. Generate marksheet
5. Download or print

Features:
• Custom templates
• Automated calculations
• Grades and percentages
• Rank display`
                }
            ]
        },
        {
            category: 'Communication',
            items: [
                {
                    title: 'Notifications',
                    content: `Send Notifications:
1. Click Notification
2. Create announcement
3. Choose recipients:
   • All
   • Specific Class
   • Individual
4. Write message
5. Add attachments (optional)
6. Schedule or send
7. Track delivery

Real-time push notifications to mobile app.`
                },
                {
                    title: 'Email & Chat',
                    content: `Send Email:
1. Communication → Mail
2. Compose email
3. Select recipients
4. Add subject/message
5. Attach files
6. Send

Chat:
1. Communication → Chat
2. Select contact
3. Send messages
4. Share files
5. View history

Group chat supported.`
                },
                {
                    title: 'Video Meetings',
                    content: `Conduct Meetings:
1. Communication → Meeting
2. Schedule or instant meeting
3. Add participants
4. Start meeting
5. Use video/audio
6. Screen sharing
7. Record (optional)

Integration with major platforms.`
                }
            ]
        },
        {
            category: 'Reports & Analytics',
            items: [
                {
                    title: 'Student Reports',
                    content: `Generate Student Reports:
1. Report → Student Report
2. Select filters
3. View reports:
   • Enrollment statistics
   • Demographics
   • Performance
   • Attendance trends
4. Export data

Customizable report templates.`
                },
                {
                    title: 'Financial Reports',
                    content: `View Financial Reports:
1. Report → Fee Report
2. Analyze:
   • Collection summary
   • Outstanding dues
   • Payment trends
   • Class-wise analysis
3. Export for accounting

Multiple date range options.`
                },
                {
                    title: 'Academic Performance',
                    content: `Track Academic Performance:
1. Report → Academic Performance
2. View:
   • Student progress
   • Subject-wise analysis
   • Class averages
   • Topper lists
3. Compare across terms
4. Identify improvement areas

Visual charts and graphs included.`
                }
            ]
        },
        {
            category: 'Settings & Profile',
            items: [
                {
                    title: 'Account Settings',
                    content: `Manage Account Settings:
1. Click profile icon
2. Select Settings
3. Configure:
   • Personal information
   • Contact details
   • Preferences
   • Notification settings
4. Save changes

Email verification required for changes.`
                },
                {
                    title: 'Password Update',
                    content: `Change Password:
1. Settings or Profile
2. Click "Change Password"
3. Enter:
   • Current password
   • New password
   • Confirm password
4. Save

Password must meet security requirements.`
                },
                {
                    title: 'Profile Management',
                    content: `Update Profile:
1. Click profile icon
2. Select Profile
3. Edit:
   • Personal details
   • Photo
   • Contact info
   • Professional details
4. Save

Profile visible to other users based on role.`
                }
            ]
        },
        {
            category: 'Troubleshooting',
            items: [
                {
                    title: 'Common Issues',
                    content: `Login Issues:
• Verify credentials
• Use "Forgot Password"
• Clear browser cache
• Try different browser
• Contact administrator

Page Not Loading:
• Check internet
• Refresh page (F5)
• Clear cache
• Try incognito mode
• Check maintenance status

Data Not Saving:
• Fill required fields
• Check internet
• Look for validation errors
• Try again
• Contact support`
                },
                {
                    title: 'Browser Compatibility',
                    content: `Recommended Browsers:
• Google Chrome (latest)
• Mozilla Firefox (latest)
• Microsoft Edge (latest)
• Safari (latest)

System Requirements:
• Modern web browser
• Stable internet
• Resolution: 1280x720+

For best experience, keep browser updated.`
                },
                {
                    title: 'Getting Help',
                    content: `Contact Support:
• Email: support@scholiq.com
• Help & Support module
• School administrator

Report Bugs:
1. Use Help & Support
2. Provide details
3. Include screenshots
4. Mention browser/device

Response within 24 hours.`
                }
            ]
        }
    ];

    const filterContent = (items) => {
        if (!searchTerm) return items;
        return items.filter(item =>
            item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.content.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const currentSection = manualSections[selectedTab];
    const filteredItems = currentSection ? filterContent(currentSection.items) : [];

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
                sx: { backdropFilter: 'blur(5px)' }
            }}
        >
            <Fade in={open}>
                <Box sx={modalStyle}>
                    {/* Header */}
                    <Box
                        sx={{
                            p: 3,
                            borderBottom: '1px solid rgba(0,0,0,0.1)',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <MenuBookIcon sx={{ fontSize: 40 }} />
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                        User Manual
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                        Comprehensive guide to Scholiq School Management System
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton
                                onClick={onClose}
                                sx={{
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        transform: 'rotate(90deg)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Tabs */}
                    <Box
                        sx={{
                            borderBottom: '1px solid rgba(0,0,0,0.1)',
                            bgcolor: '#f8fafc',
                            overflowX: 'auto',
                        }}
                    >
                        <Tabs
                            value={selectedTab}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                '& .MuiTab-root': {
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    minWidth: 120,
                                    py: 2,
                                }
                            }}
                        >
                            {manualSections.map((section, index) => (
                                <Tab key={index} label={section.category} />
                            ))}
                        </Tabs>
                    </Box>

                    {/* Search */}
                    <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,0.1)', bgcolor: '#f8fafc' }}>
                        <TextField
                            fullWidth
                            placeholder="Search in user manual..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    bgcolor: 'white',
                                }
                            }}
                        />
                    </Box>

                    {/* Content */}
                    <Box
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            p: 3,
                        }}
                    >
                        {filteredItems.length > 0 ? (
                            filteredItems.map((item, index) => (
                                <Accordion
                                    key={index}
                                    expanded={expandedAccordion === `panel${index}`}
                                    onChange={handleAccordionChange(`panel${index}`)}
                                    sx={{
                                        mb: 2,
                                        borderRadius: 2,
                                        '&:before': { display: 'none' },
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            bgcolor: '#f8fafc',
                                            borderRadius: 2,
                                            '&:hover': {
                                                bgcolor: '#e2e8f0',
                                            },
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {item.title}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 3 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                whiteSpace: 'pre-line',
                                                lineHeight: 1.8,
                                                color: 'text.secondary',
                                            }}
                                        >
                                            {item.content}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            ))
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography variant="h6" color="text.secondary">
                                    No results found for "{searchTerm}"
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Try different keywords or browse other categories
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Footer */}
                    <Box
                        sx={{
                            p: 2,
                            borderTop: '1px solid rgba(0,0,0,0.1)',
                            bgcolor: '#f8fafc',
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Need more help? Contact support at{' '}
                            <Typography
                                component="span"
                                sx={{
                                    color: 'primary.main',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                support@scholiq.com
                            </Typography>
                        </Typography>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};

export default UserManualModal;

